<template>
  <div class="orders-distribute-container">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>订单分发</span>
      </div>
      <div class="header-actions">
        <el-button @click="goBack" icon="el-icon-back">返回</el-button>
      </div>
    </div>
    
    <!-- 主订单信息 -->
    <el-card class="main-order-info" v-loading="loading">
      <div slot="header">
        <span>原始订单信息</span>
      </div>
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="info-item">
            <span class="label">订单编号：</span>
            <span class="value">{{ mainOrder.order_id || '--' }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <span class="label">订单标题：</span>
            <span class="value">{{ mainOrder.order_title || '--' }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <span class="label">总金额：</span>
            <span class="value">{{ formatAmount(mainOrder.price_total || 0) }} 元</span>
          </div>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="info-item">
            <span class="label">客户名称：</span>
            <span class="value">{{ mainOrder.customer_name || '--' }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <span class="label">联系人：</span>
            <span class="value">{{ mainOrder.contact_person || '--' }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <span class="label">联系电话：</span>
            <span class="value">{{ mainOrder.contact_phone || '--' }}</span>
          </div>
        </el-col>
      </el-row>
    </el-card>
    
    <!-- 订单材料清单 -->
    <el-card class="order-materials-card" v-loading="loading">
      <div slot="header">
        <span>材料清单</span>
        <div style="float: right;">
          <span>总产品数: {{ mainOrder.products_total || orderItems.length || 0 }} 种</span>
          <span style="margin-left: 20px;">总金额: {{ formatAmount(mainOrder.price_total || 0) }} 元</span>
        </div>
      </div>
      
      <el-table
        :data="orderItems"
        border
        style="width: 100%"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column type="index" width="50"></el-table-column>
        <el-table-column label="产品名称" prop="product_name" min-width="150">
          <template slot-scope="scope">
            {{ scope.row.product_name || scope.row.materials_name || scope.row.name || '--' }}
          </template>
        </el-table-column>
        <el-table-column label="单位" width="80">
          <template slot-scope="scope">
            {{ scope.row.product_unit || scope.row.unit || '--' }}
          </template>
        </el-table-column>
        <el-table-column label="数量" width="100">
          <template slot-scope="scope">
            {{ scope.row.quantity || 0 }}
          </template>
        </el-table-column>
        <el-table-column label="单价(元)" width="100">
          <template slot-scope="scope">
            {{ scope.row.product_price || scope.row.price || scope.row.before_price || 0 }}
          </template>
        </el-table-column>
        <el-table-column label="供应商" min-width="180">
          <template slot-scope="scope">
            <span v-if="scope.row.unit_info">
              {{ scope.row.unit_info.unit_name || '--' }}
            </span>
            <span v-else>
              {{ scope.row.supplier_name || '未知供应商' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="备注" min-width="120">
          <template slot-scope="scope">
            {{ scope.row.remark || scope.row.category || '--' }}
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    
    <!-- 分发分组信息 -->
    <div class="distribute-info" v-loading="loading">
      <h3>按供应商分组分发（共 {{ supplierGroups.length }} 个供应商）</h3>
      
      <div v-if="supplierGroups.length === 0 && !loading" class="empty-data">
        <i class="el-icon-warning"></i>
        <p>无法找到可分发的供应商信息，请确保原始订单中的材料包含供应商信息</p>
      </div>
      
      <!-- 供应商分组展示 -->
      <div v-for="(group, index) in supplierGroups" :key="index" class="supplier-group">
        <el-card>
          <div slot="header">
            <span><i class="el-icon-s-shop"></i> 供应商：{{ group.supplier_name }}</span>
            <div style="float: right;">
              <el-button 
                v-permission="'orders:orders-distribute:edit'"
                type="text" 
                icon="el-icon-edit" 
                @click="editSupplierOrder(group)"
                :disabled="!group.selected"
              >编辑联系信息</el-button>
              <el-switch
                v-model="group.selected"
                active-text="包含"
                inactive-text="排除"
                style="margin-left: 10px;"
              ></el-switch>
            </div>
          </div>
          
          <!-- 供应商材料清单 -->
          <el-table
            :data="group.items"
            border
            :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
            style="width: 100%"
          >
            <el-table-column type="index" width="50"></el-table-column>
            <el-table-column label="材料类别" min-width="100">
              <template slot-scope="scope">
                {{ scope.row.category || scope.row.remark || '其他' }}
              </template>
            </el-table-column>
            <el-table-column label="材料名称" min-width="180">
              <template slot-scope="scope">
                {{ scope.row.materials_name || scope.row.product_name || scope.row.name || '--' }}
              </template>
            </el-table-column>
            <el-table-column label="规格" min-width="120">
              <template slot-scope="scope">
                {{ scope.row.materials_spec || scope.row.product_spec || scope.row.spec || '--' }}
              </template>
            </el-table-column>
            <el-table-column label="单位" width="80">
              <template slot-scope="scope">
                {{ scope.row.unit || scope.row.product_unit || '个' }}
              </template>
            </el-table-column>
            <el-table-column label="单价(元)" width="100">
              <template slot-scope="scope">
                {{ scope.row.before_price || scope.row.product_price || scope.row.price || 0 }}
              </template>
            </el-table-column>
            <el-table-column label="税率" width="80">
              <template slot-scope="scope">
                {{ formatTaxRate(scope.row.tax_rate || scope.row.product_tax_rate || 0) }}
              </template>
            </el-table-column>
            <el-table-column label="含税价(元)" width="120">
              <template slot-scope="scope">
                {{ scope.row.price || scope.row.product_price || 0 }}
              </template>
            </el-table-column>
            <el-table-column label="数量" width="120">
              <template slot-scope="scope">
                <el-input-number 
                  v-model="scope.row.quantity" 
                  :min="1" 
                  :max="scope.row.max_quantity || 999999" 
                  :precision="4"
                  :step="0.0001"
                  size="small"
                  :disabled="!group.selected"
                  @change="recalculateTotal(group)"
                ></el-input-number>
              </template>
            </el-table-column>
          </el-table>
          
          <div class="group-summary">
            <span>材料种类: {{ group.items.length }} 种</span>
            <span>总数量: {{ formatQuantity(group.totalQuantity) }}</span>
            <span>总金额: {{ formatAmount(group.totalPrice) }} 元</span>
          </div>
        </el-card>
      </div>
    </div>
    
    <!-- 底部操作区域 -->
    <div class="bottom-actions">
      <el-button @click="goBack">取消</el-button>
      <el-button type="warning" @click="previewDistribution" :disabled="selectedGroupCount === 0">
        分发预览
      </el-button>
      <el-button v-permission="'orders:orders-distribute:confirm'" type="primary" @click="distributeOrders" :loading="submitLoading" :disabled="selectedGroupCount === 0">
        确认分发 ({{ selectedGroupCount }} 个供应商)
      </el-button>
    </div>
    
    <!-- 编辑供应商订单对话框 -->
    <el-dialog
      v-dialog-drag
      title="编辑子订单信息"
      :visible.sync="editDialogVisible"
      width="50%"
      :close-on-click-modal="false"
    >
      <el-form :model="editingOrder" label-width="100px" v-if="editingOrder">
        <el-form-item label="订单标题">
          <el-input v-model="editingOrder.order_title" placeholder="请输入订单标题"></el-input>
        </el-form-item>
        <el-form-item label="联系人">
          <el-input v-model="editingOrder.contact_person" placeholder="请输入联系人"></el-input>
        </el-form-item>
        <el-form-item label="联系电话">
          <el-input v-model="editingOrder.contact_phone" placeholder="请输入联系电话"></el-input>
        </el-form-item>
        <el-form-item label="联系地址">
          <el-input v-model="editingOrder.contact_address" placeholder="请输入联系地址"></el-input>
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            type="textarea"
            :rows="3"
            placeholder="请输入备注信息"
            v-model="editingOrder.remarks"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveEditingOrder">确 定</el-button>
      </div>
    </el-dialog>
    
    <!-- 分发预览对话框 -->
    <el-dialog
      v-dialog-drag
      title="分发预览"
      :visible.sync="previewDialogVisible"
      width="70%"
      :close-on-click-modal="false"
    >
      <div v-if="previewOrders.length === 0" class="empty-preview">
        <i class="el-icon-warning-outline"></i>
        <p>暂无可预览的订单，请至少选择一个供应商进行分发</p>
      </div>
      
      <div v-for="(order, index) in previewOrders" :key="index" class="preview-order">
        <h4>子订单 {{ index + 1 }}: {{ order.supplier_name }}</h4>
        <el-divider></el-divider>
        
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="info-item">
              <span class="label">订单标题：</span>
              <span class="value">{{ order.order_title }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <span class="label">供应商：</span>
              <span class="value">{{ order.supplier_name }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <span class="label">联系人：</span>
              <span class="value">{{ order.contact_person }}</span>
            </div>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="info-item">
              <span class="label">联系电话：</span>
              <span class="value">{{ order.contact_phone }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <span class="label">材料种类：</span>
              <span class="value">{{ order.order_items.length }} 种</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <span class="label">总金额：</span>
              <span class="value">{{ formatAmount(calculateOrderTotal(order)) }} 元</span>
            </div>
          </el-col>
        </el-row>
        
        <div v-if="order.remarks" class="remarks">
          <span class="label">备注：</span>
          <span>{{ order.remarks }}</span>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="previewDialogVisible = false">关 闭</el-button>
        <el-button type="primary" @click="confirmPreview">确认分发</el-button>
      </div>
    </el-dialog>
    
    <!-- 分发结果对话框 -->
    <el-dialog
      v-dialog-drag
      title="分发结果"
      :visible.sync="resultDialogVisible"
      width="50%"
      :close-on-click-modal="false"
    >
      <div class="result-content">
        <i class="el-icon-success result-icon"></i>
        <h3 class="result-title">订单分发成功</h3>
        <p class="result-info">已成功将订单分发给 {{ distributedCount }} 个供应商</p>
        <div class="result-detail">
          <p>分发结果：</p>
          <el-table 
            :data="distributedItems" 
            border 
            style="width: 100%; margin-top: 10px;"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
          >
            <el-table-column prop="supplier_name" label="供应商" min-width="120"></el-table-column>
            <el-table-column prop="order_id" label="订单号" min-width="150"></el-table-column>
            <el-table-column prop="status" label="状态" width="100">
              <template slot-scope="scope">
                <el-tag
                  :type="scope.row.status === '成功' ? 'success' : 
                         scope.row.status === '处理中' ? 'warning' : 'info'"
                >{{ scope.row.status }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="viewOrdersList">返回订单列表</el-button>
        <el-button type="primary" @click="resultDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { get_order_detail, distribute_order } from '@/api/modules/orders.js';

export default {
  name: 'OrdersDistribute',
  data() {
    return {
      orderId: null,
      loading: false,
      submitLoading: false,
      mainOrder: {}, // 主订单信息
      orderItems: [], // 订单材料项
      supplierGroups: [], // 按供应商分组后的数据
      editDialogVisible: false,
      editingOrder: null,
      editingGroupIndex: -1,
      previewDialogVisible: false,
      previewOrders: [],
      resultDialogVisible: false,
      distributedCount: 0,
      distributedItems: [],
      currentUnitId: null // 当前用户单位ID
    };
  },
  computed: {
    // 计算选中的供应商组数量
    selectedGroupCount() {
      return this.supplierGroups.filter(group => group.selected).length;
    }
  },
  created() {
    // 从路由参数获取订单ID
    this.orderId = this.$route.params.id;
    if (!this.orderId) {
      this.$message.error('缺少订单ID参数');
      this.goBack();
      return;
    }
    
    // 获取当前用户单位ID
    this.getCurrentUnitId();
    
    // 加载订单信息
    this.getOrderDetail();
  },
  methods: {
    // 获取当前用户的单位ID
    getCurrentUnitId() {
      try {
        // 尝试从store中获取用户信息
        const store = this.$store || {};
        const state = store.state || {};
        
// 尝试从不同可能的路径获取用户信息
        let userInfo = null;
        
        if (state.user && state.user.userInfo) {
          userInfo = state.user.userInfo;
} else if (state.userInfo) {
          userInfo = state.userInfo;
} else if (state.auth && state.auth.userInfo) {
          userInfo = state.auth.userInfo;
}
        
        // 如果store中找不到，尝试从localStorage获取
        if (!userInfo || !userInfo.unitId) {
          const localUserInfo = localStorage.getItem('userInfo');
          if (localUserInfo) {
            try {
              userInfo = JSON.parse(localUserInfo);
} catch (e) {
              console.error('解析localStorage中的userInfo失败:', e);
            }
          }
        }
        
        if (userInfo && userInfo.unitId) {
          this.currentUnitId = parseInt(userInfo.unitId);
} else {
          console.warn('未能获取用户单位ID，将使用默认值1进行测试');
          this.currentUnitId = 1; // 使用默认值便于测试
        }
      } catch (err) {
        console.error('获取用户单位ID出错:', err);
        console.warn('出错时使用默认值1进行测试');
        this.currentUnitId = 1;
      }
    },
    // 获取订单详情
    getOrderDetail() {
      this.loading = true;
      get_order_detail(this.orderId)
        .then(response => {
          if (response.data && response.data.success) {
            const orderData = response.data.data;
// 保存主订单信息
            this.mainOrder = orderData;
            
            // 获取订单材料项
            if (orderData.OrderItems && Array.isArray(orderData.OrderItems)) {
              // 新的API返回格式
              this.orderItems = orderData.OrderItems;
} else if (orderData.order_items && Array.isArray(orderData.order_items)) {
              // 旧的常见格式
              this.orderItems = orderData.order_items;
} else if (orderData.items && Array.isArray(orderData.items)) {
              // 另一种旧格式
              this.orderItems = orderData.items;
} else {
              this.orderItems = [];
              console.warn('未找到有效的材料清单数据');
            }
            
            // 按供应商分组
            this.groupBySupplier();
          } else {
            this.$message.error(response.data?.message || '获取订单详情失败');
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('获取订单详情失败:', error);
          this.$message.error('获取订单详情失败: ' + (error.message || '未知错误'));
          this.loading = false;
        });
    },
    
    // 按供应商分组
    groupBySupplier() {
      // 创建供应商组映射
      const groupMap = {};
      
      // 尝试从OrderItems中获取数据
      let orderItemsData = [];
      if (this.mainOrder.OrderItems && Array.isArray(this.mainOrder.OrderItems)) {
orderItemsData = this.mainOrder.OrderItems;
      } else {
orderItemsData = this.orderItems;
      }
      
// 遍历材料项，按供应商ID分组
      orderItemsData.forEach(item => {
        // 获取供应商信息
        let supplierId, supplierName, supplierContact, supplierPhone;
        
        // 判断是否有unit_info字段（新数据结构）
        if (item.unit_info) {
          supplierId = item.unit_info.id || item.unit_info.unit_id || item.product_supplier_id || '';
          supplierName = item.unit_info.unit_name || '未知供应商';
          supplierContact = item.unit_info.contact_person || '';
          supplierPhone = item.unit_info.phone || '';
        } else {
          // 旧数据结构
          supplierId = item.supplier_id || item.product_supplier_id || '';
          supplierName = item.supplier_name || '未知供应商';
        }
        
        // 确保supplierId不为空
        if (!supplierId) {
          console.warn('发现材料项没有供应商ID:', item);
          supplierId = `unknown-${supplierName}-${Date.now()}`;
        }
        
        const key = `${supplierId}-${supplierName}`;
        
        // 如果是首次遇到该供应商，创建新分组
        if (!groupMap[key]) {
          groupMap[key] = {
            supplier_id: supplierId,
            supplier_name: supplierName,
            items: [],
            selected: true, // 默认选中
            totalQuantity: 0,
            totalPrice: 0,
            contact_person: supplierContact || this.mainOrder.contact_person || '',
            contact_phone: supplierPhone || this.mainOrder.contact_phone || '',
            contact_address: this.mainOrder.contact_address || '',
            order_title: `${this.mainOrder.order_title || '订单'}-${supplierName}`,
            remarks: ''
          };
        }
        
        // 标准化物料数据
        const materialItem = {
          ...item,
          // 确保字段名一致性
          materials_name: item.product_name || item.materials_name || item.name,
          materials_spec: item.product_spec || item.materials_spec || item.spec || '',
          category: item.remark || item.category || '',
          unit: item.product_unit || item.unit || '个',
          quantity: item.quantity || 1,
          max_quantity: parseFloat(item.quantity) || 1,
          price: item.product_price || item.price || 0,
          before_price: (item.product_price && !item.product_tax_rate) ? 
            item.product_price : 
            (item.before_price || item.price_without_tax || 0),
          tax_rate: item.product_tax_rate || item.tax_rate || 0,
          supplier_id: supplierId,
          supplier_name: supplierName
        };
        
        // 添加到供应商分组中
        groupMap[key].items.push(materialItem);
      });
      
      // 转换为数组
      this.supplierGroups = Object.values(groupMap);
      
      // 计算每个分组的总数量和总价
      this.supplierGroups.forEach(group => {
        this.recalculateTotal(group);
      });
      
},
    
    // 重新计算分组总数和总价
    recalculateTotal(group) {
      if (!group || !group.items) return;
      
      // 计算总数量（支持4位小数）
      group.totalQuantity = group.items.reduce((sum, item) => {
        return sum + (parseFloat(item.quantity) || 0);
      }, 0);
      
      // 计算总价
      group.totalPrice = group.items.reduce((sum, item) => {
        return sum + ((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0));
      }, 0).toFixed(2);
    },
    
    // 计算订单总价
    calculateOrderTotal(order) {
      if (!order || !order.order_items) return 0;
      
      const total = order.order_items.reduce((sum, item) => {
        return sum + ((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0));
      }, 0);
      
      return total;
    },

    // 格式化金额（带千位分隔符）
    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.00';
      const num = parseFloat(amount);
      if (isNaN(num)) return '0.00';
      return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    
    // 格式化数量（保留4位小数，去除末尾无意义的0）
    formatQuantity(quantity) {
      if (quantity === undefined || quantity === null) return '0';
      const num = parseFloat(quantity);
      if (isNaN(num)) return '0';
      // 先格式化为4位小数，然后去除末尾的0和小数点（如果有）
      const formatted = num.toFixed(4);
      return formatted.replace(/\.?0+$/, '') || '0';
    },
    
    // 编辑供应商订单信息
    editSupplierOrder(group) {
      const index = this.supplierGroups.findIndex(g => g.supplier_name === group.supplier_name);
      if (index === -1) return;
      
      this.editingGroupIndex = index;
      this.editingOrder = {
        order_title: group.order_title,
        contact_person: group.contact_person,
        contact_phone: group.contact_phone,
        contact_address: group.contact_address,
        remarks: group.remarks || ''
      };
      
      this.editDialogVisible = true;
    },
    
    // 保存编辑的订单信息
    saveEditingOrder() {
      if (this.editingGroupIndex === -1 || !this.editingOrder) {
        this.editDialogVisible = false;
        return;
      }
      
      // 更新分组信息
      const group = this.supplierGroups[this.editingGroupIndex];
      group.order_title = this.editingOrder.order_title;
      group.contact_person = this.editingOrder.contact_person;
      group.contact_phone = this.editingOrder.contact_phone;
      group.contact_address = this.editingOrder.contact_address;
      group.remarks = this.editingOrder.remarks;
      
      this.$message.success('子订单信息已更新');
      this.editDialogVisible = false;
    },
    
    // 预览分发结果
    previewDistribution() {
      // 检查是否有选中的供应商
      if (this.selectedGroupCount === 0) {
        this.$message.warning('请至少选择一个供应商进行分发');
        return;
      }
      
      // 准备预览数据
      this.previewOrders = this.prepareChildOrders();
      this.previewDialogVisible = true;
    },
    
    // 确认预览并分发
    confirmPreview() {
      this.previewDialogVisible = false;
      this.distributeOrders();
    },
    
    // 分发订单
    distributeOrders() {
      // 检查是否有选中的供应商
      if (this.selectedGroupCount === 0) {
        this.$message.warning('请至少选择一个供应商进行分发');
        return;
      }
      
      this.$confirm('确认将订单分发给选中的 ' + this.selectedGroupCount + ' 个供应商?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.submitLoading = true;
        
        try {
          // 准备子订单数据
          const childOrders = this.prepareChildOrders();
// 验证每个子订单是否包含必要字段
          const requiredFields = ['contract_id', 'supplier_id', 'customer_id', 'create_id'];
          let missingFieldsFound = false;
          
          childOrders.forEach((order, index) => {
            const missingFields = requiredFields.filter(field => {
              const value = order[field];
              return value === undefined || value === null || value === '' || (typeof value === 'number' && isNaN(value));
            });
            
            if (missingFields.length > 0) {
              console.error(`子订单 ${index + 1} (${order.supplier_name}) 缺少必要字段: ${missingFields.join(', ')}`);
              console.error('子订单数据:', JSON.stringify(order));
              missingFieldsFound = true;
            }
          });
          
          if (missingFieldsFound) {
            this.$message.error('订单数据不完整，请确保包含所有必要字段');
            this.submitLoading = false;
            return;
          }
          
          // 检查是否有为0的ID字段
          let hasZeroId = false;
          childOrders.forEach((order, index) => {
            if (order.supplier_id === 0 || order.customer_id === 0 || order.create_id === 0) {
              console.warn(`子订单 ${index + 1} (${order.supplier_name}) 有ID字段为0:`, 
                `supplier_id=${order.supplier_id}, customer_id=${order.customer_id}, create_id=${order.create_id}`);
              hasZeroId = true;
            }
          });
          
          if (hasZeroId) {
            console.warn('存在ID字段为0的子订单，这可能导致保存失败');
          }
          
          // 调用分发API
distribute_order(this.orderId, childOrders)
            .then(response => {
if (response.data && response.data.success) {
                // 准备分发结果数据
                this.distributedCount = childOrders.length;
                this.distributedItems = childOrders.map((order) => ({
                  supplier_name: order.supplier_name,
                  order_id: '待生成',
                  status: '处理中'
                }));
                
                // 如果响应中包含新订单ID，则更新显示
                if (response.data.data && Array.isArray(response.data.data)) {
                  const orderIds = response.data.data;
                  orderIds.forEach((id, index) => {
                    if (index < this.distributedItems.length) {
                      this.distributedItems[index].order_id = id || '处理成功但未返回ID';
                      this.distributedItems[index].status = '成功';
                    }
                  });
                }
                
                // 显示成功消息
                this.$message.success(response.data.message || `成功将订单分发给 ${this.distributedCount} 个供应商`);
                
                // 显示结果对话框
                this.resultDialogVisible = true;
              } else {
                // 处理错误情况
                this.$message.error(response.data?.message || '订单分发失败');
                
                // 如果有部分成功的情况，也显示结果对话框
                if (response.data && response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
                  this.distributedCount = response.data.data.length;
                  this.distributedItems = response.data.data.map((id, index) => ({
                    supplier_name: childOrders[index]?.supplier_name || `供应商 ${index + 1}`,
                    order_id: id || '未知',
                    status: '成功'
                  }));
                  this.resultDialogVisible = true;
                }
              }
              this.submitLoading = false;
            })
            .catch(error => {
              console.error('订单分发失败:', error);
              this.$message.error('订单分发失败: ' + (error.message || '未知错误'));
              this.submitLoading = false;
            });
        } catch (e) {
          console.error('分发订单失败:', e);
          this.$message.error('分发订单失败: ' + (e.message || '未知错误'));
          this.submitLoading = false;
        }
      }).catch(() => {
        this.$message.info('已取消分发');
      });
    },
    
    // 准备子订单数据
    prepareChildOrders() {
      // 筛选出选中的供应商组
      const selectedGroups = this.supplierGroups.filter(group => group.selected);
      
      // 从主订单中提取必要字段，确保都有默认值
      const contractId = parseInt(this.mainOrder.contract_id) || 0;
      
      // 为每个选中的供应商组创建子订单
      return selectedGroups.map(group => {
        // 处理供应商ID - 确保是数字类型
        let supplierId;
        try {
          // 尝试从字符串转换为数字
          if (typeof group.supplier_id === 'string' && group.supplier_id.startsWith('unknown-')) {
            // 对于临时ID，使用时间戳
            supplierId = Date.now();
          } else {
            supplierId = parseInt(group.supplier_id);
            // 如果转换结果为NaN，则使用时间戳
            if (isNaN(supplierId)) {
              supplierId = Date.now();
            }
          }
        } catch (e) {
          console.error('供应商ID转换错误:', e);
          supplierId = Date.now();
        }
        
// 转换订单项为新API格式
        const orderItems = group.items.map(item => ({
          category: item.category || '其他',
          materialsName: item.materials_name || item.product_name || '未命名产品',
          materialsSpec: item.materials_spec || item.specification || '',
          unit: item.unit || item.product_unit || '个',
          quantity: parseFloat(item.quantity) || 1,
          beforePrice: parseFloat(item.before_price) || parseFloat(item.product_price) || 0,
          taxRate: parseFloat(item.tax_rate) || parseFloat(item.product_tax_rate) || 0,
          price: parseFloat(item.price) || parseFloat(item.product_price) || 0,
          productSupplierId: supplierId
        }));
        
        // 计算总数量和总价
        const productsTotal = orderItems.reduce((sum, item) => sum + item.quantity, 0);
        const priceTotal = orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        
        // 创建子订单对象（新API格式）
        const childOrder = {
          orderTitle: group.order_title || `订单-${group.supplier_name}`,
          contractId: contractId,
          contractType: "SALES",
          orderType: "01",
          supplierId: supplierId,
          contactPerson: group.contact_person || '',
          contactPhone: group.contact_phone || '',
          contactAddress: group.contact_address || '',
          productsTotal: productsTotal,
          priceTotal: priceTotal,
          orderItems: orderItems,
          // 保留父订单ID用于关联
          parent_order_id: parseInt(this.orderId) || 0
        };
        
        return childOrder;
      });
    },
    
    // 返回订单列表
    viewOrdersList() {
      this.resultDialogVisible = false;
      this.$router.push('/orders/customer-list');
    },
    
    // 返回上一页
    goBack() {
      if (this.$route.query.from === 'orders-list') {
        this.$router.push('/orders/customer-list');
      } else {
        this.$router.go(-1);
      }
    },
    // 格式化税率显示（添加%符号）
    formatTaxRate(value) {
      if (value === null || value === undefined || value === '') return '0%';
      const num = parseFloat(value);
      if (isNaN(num)) return '0%';
      // 如果值小于1，说明是小数形式（如0.13），转换为百分比形式（13）
      if (num < 1 && num > 0) {
        return `${Math.round(num * 100)}%`;
      }
      // 如果值大于等于1，说明已经是百分比形式（如13），直接返回
      return `${Math.round(num)}%`;
    }
  }
};
</script>

<style scoped>
.orders-distribute-container {
  padding: 20px;
  background-color: #fff;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-title {
  display: flex;
  align-items: center;
}

.title-mark {
  width: 4px;
  height: 18px;
  background-color: #2c73eb;
  margin-right: 8px;
}

.header-title span {
  font-size: 18px;
  font-weight: 500;
  color: #303133;
}

.main-order-info {
  margin-bottom: 20px;
}

.info-item {
  margin-bottom: 10px;
  line-height: 1.5;
}

.info-item .label {
  color: #606266;
  margin-right: 5px;
}

.info-item .value {
  color: #303133;
  font-weight: 500;
}

.distribute-info {
  margin-bottom: 20px;
}

.distribute-info h3 {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 3px solid #2c73eb;
}

.supplier-group {
  margin-bottom: 20px;
}

.group-summary {
  margin-top: 15px;
  text-align: right;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.group-summary span {
  margin-left: 20px;
  font-weight: 500;
}

.empty-data {
  padding: 30px;
  text-align: center;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.empty-data i {
  font-size: 48px;
  color: #e6a23c;
  margin-bottom: 10px;
}

.bottom-actions {
  margin-top: 30px;
  text-align: center;
}

.bottom-actions .el-button {
  min-width: 120px;
}

/* 预览对话框样式 */
.preview-order {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #EBEEF5;
}

.preview-order:last-child {
  border-bottom: none;
}

.preview-order h4 {
  font-size: 16px;
  margin-bottom: 10px;
  color: #303133;
}

.empty-preview {
  text-align: center;
  padding: 30px 0;
}

.empty-preview i {
  font-size: 48px;
  color: #909399;
  margin-bottom: 15px;
}

.remarks {
  margin-top: 10px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.remarks .label {
  font-weight: bold;
  color: #606266;
}

/* 结果对话框样式 */
.result-content {
  text-align: center;
  padding: 20px 0;
}

.result-icon {
  font-size: 60px;
  color: #67C23A;
  margin-bottom: 20px;
}

.result-title {
  font-size: 20px;
  color: #303133;
  margin-bottom: 10px;
}

.result-info {
  font-size: 16px;
  color: #606266;
  margin-bottom: 20px;
}

.result-detail {
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.result-detail p {
  font-weight: bold;
  margin-bottom: 10px;
}

.result-detail ul {
  padding-left: 20px;
}

.result-detail li {
  margin-bottom: 5px;
}
</style>